import { useEffect, useRef, useState } from "react";
import { Eye, Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Btn, Card, FieldLabel } from "@/components/admin/ui";

const themes = [
  ["midnight", "Midnight Black", "#ff4b32"],
  ["neon", "Neon Purple", "#ba5cff"],
  ["clean", "Clean White", "#111827"],
  ["spicy", "SPICY Red", "#e11d2a"],
];
const defaults = {
  theme: "midnight",
  accent: "#ff4b32",
  background: "solid",
  glass: false,
  intensity: 35,
};

export default function Appearance() {
  const [value, setValue] = useState(defaults);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const savedRef = useRef(defaults);

  useEffect(() => {
    api
      .get("/admin/appearance")
      .then(({ data }) => {
        setValue({ ...defaults, ...data });
        savedRef.current = { ...defaults, ...data };
        setLoaded(true);
      })
      .catch((error) => toast.error(errMsg(error)));
  }, []);

  useEffect(
    () => () => {
      window.dispatchEvent(
        new CustomEvent("admin-appearance", { detail: savedRef.current }),
      );
    },
    [],
  );

  const applyPreview = (next) => {
    setValue(next);
    window.dispatchEvent(new CustomEvent("admin-appearance", { detail: next }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/appearance", value);
      const saved = { ...defaults, ...data };
      savedRef.current = saved;
      applyPreview(saved);
      toast.success("Admin appearance saved.");
    } catch (error) {
      toast.error(errMsg(error));
    }
    setSaving(false);
  };

  if (!loaded) {
    return (
      <div className="py-20 text-center text-zinc-400">
        <Loader2 className="animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-appearance-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">
          Settings
        </p>
        <h1 className="text-2xl font-bold mt-1">Appearance</h1>
        <p className="text-sm text-zinc-500 mt-1">
          These preferences apply only to your admin dashboard.
        </p>
      </div>

      <Card
        title="Admin theme"
        description="Choose an interface direction, then fine-tune the accent."
      >
        <div
          className="grid sm:grid-cols-2 gap-3"
          data-testid="appearance-theme-options"
        >
          {themes.map(([id, label, accent]) => (
            <button
              key={id}
              type="button"
              onClick={() => applyPreview({ ...value, theme: id, accent })}
              className={`appearance-theme-option ${value.theme === id ? "is-active" : ""}`}
              data-testid={`appearance-theme-${id}`}
            >
              <span
                className="appearance-theme-swatch"
                style={{ background: accent }}
              />
              <span>{label}</span>
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <label>
            <FieldLabel>Custom accent</FieldLabel>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={value.accent}
                onChange={(event) =>
                  applyPreview({ ...value, accent: event.target.value })
                }
                className="h-10 w-12 bg-transparent"
                data-testid="appearance-accent-picker"
              />
              <span className="font-mono text-sm">
                {value.accent.toUpperCase()}
              </span>
            </div>
          </label>
          <label>
            <FieldLabel>Background</FieldLabel>
            <select
              value={value.background}
              onChange={(event) =>
                applyPreview({ ...value, background: event.target.value })
              }
              className="admin-select"
              data-testid="appearance-background-select"
            >
              <option value="solid">Solid</option>
              <option value="gradient">Subtle gradient</option>
              <option value="texture">Minimal texture</option>
            </select>
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <label className="flex items-center gap-3 text-sm pt-5">
            <input
              type="checkbox"
              checked={value.glass}
              onChange={(event) =>
                applyPreview({ ...value, glass: event.target.checked })
              }
              data-testid="appearance-glass-toggle"
            />
            Glass-effect panels
          </label>
          <label>
            <FieldLabel>Background intensity · {value.intensity}%</FieldLabel>
            <input
              type="range"
              min="0"
              max="100"
              value={value.intensity}
              onChange={(event) =>
                applyPreview({
                  ...value,
                  intensity: Number(event.target.value),
                })
              }
              className="w-full accent-[var(--admin-accent)]"
              data-testid="appearance-intensity-range"
            />
          </label>
        </div>
      </Card>

      <Card
        title="Live preview"
        description="This preview changes as you adjust the controls."
      >
        <div
          className="appearance-preview"
          data-testid="appearance-live-preview"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.16em] opacity-60">
              SPICY3D WORLD
            </p>
            <p className="text-lg font-semibold mt-1">Store operations</p>
          </div>
          <span className="appearance-preview-badge">
            <Eye size={13} /> Preview
          </span>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Btn
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="appearance-save-button"
        >
          {saving ? (
            <Loader2 size={15} className="animate-spin" />
          ) : (
            <Save size={15} />
          )}{" "}
          Save preferences
        </Btn>
        <Btn
          type="button"
          variant="secondary"
          onClick={() => applyPreview(defaults)}
          data-testid="appearance-restore-button"
        >
          <RotateCcw size={15} /> Restore default
        </Btn>
      </div>
    </div>
  );
}
